import React, { useEffect } from "react";
import ScrollLeftRightArrows, { ArrowContainer } from "../ScrollLeftRightArrows";
import { logError } from "../../../helpers/logging";

export interface ArrowNavigatorProps {
  scrollAdjustment?: number;
  scrollInPixels?: number;
  horizontalContainer: React.MutableRefObject<HTMLDivElement | null>;
  initialOffset?: number;
}

const ArrowNavigator = ({
  scrollAdjustment,
  scrollInPixels,
  horizontalContainer,
  initialOffset,
}: ArrowNavigatorProps) => {
  const isScrollSetInPixels = !!scrollInPixels;

  /**
   * Handles the right scroll of the ref element when the right arrow is clicked.
   * */
  const handleRightArrowClickScroll = () => {
    const { current } = horizontalContainer;
    const { scrollLeft, clientWidth } = current || {};

    const scroll = isScrollSetInPixels
      ? (scrollLeft ?? 0) + scrollInPixels
      : (scrollLeft ?? 0) + (clientWidth ?? 0) + (scrollAdjustment ?? 0);
    try {
      if (current) {
        if (current.scrollTo) {
          current.scrollTo({
            left: scroll,
            behavior: "smooth",
          });
        } else {
          // Scrolls using a different method due to known issue in edge browser.
          // TODO: remove once this issue is resolved.
          current.scrollLeft = scroll;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      logError(error, { location: "ArrowNavigator", action: "SCROLL_RIGHT" });
    }
  };

  /**
   * Handles the left scroll of the ref element when the left arrow is clicked.
   * */
  const handleLeftArrowClickScroll = () => {
    const { current } = horizontalContainer;
    const { scrollLeft, clientWidth } = current || {};

    const scroll = isScrollSetInPixels
      ? (scrollLeft ?? 0) - scrollInPixels
      : (scrollLeft ?? 0) - (clientWidth ?? 0) - (scrollAdjustment ?? 0);
    try {
      if (current) {
        if (current.scrollTo) {
          current.scrollTo({
            left: scroll,
            behavior: "smooth",
          });
        } else {
          // Scrolls using a different method due to known issue in edge browser.
          // TODO: remove once this issue is resolved.
          current.scrollLeft = scroll;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      logError(error, { location: "ArrowNavigator", action: "SCROLL_LEFT" });
    }
  };

  useEffect(() => {
    if (initialOffset) {
      const { current } = horizontalContainer;
      try {
        if (current) {
          if (current.scrollTo) {
            current.scrollTo({ left: initialOffset });
          } else {
            current.scrollLeft = initialOffset;
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        logError(error, { location: "ArrowNavigator", action: "SETTING_OFFSET" });
      }
    }
  }, [initialOffset]);

  return (
    <ArrowContainer>
      <ScrollLeftRightArrows
        handleRightArrowClickScroll={handleRightArrowClickScroll}
        handleLeftArrowClickScroll={handleLeftArrowClickScroll}
      />
    </ArrowContainer>
  );
};

export default ArrowNavigator;
